import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Button, Alert, Tab, Nav, Card, Row, Col } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../shared/AuthContext';
import { api } from '../../shared/api';
import { CenteredLoader } from '../../components/LoadingSpinner';
import { useConfirm } from '../../shared/ConfirmContext';
import ActiveSessionsTab from './ActiveSessionsTab';

const Profile = () => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    const tab = searchParams.get('tab');
    return tab === 'security' || tab === 'sessions' ? tab : 'general';
  });
  const [imageKey, setImageKey] = useState(Date.now());

  // Profile data state
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    profile_photo: null
  });

  // Password change state
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });

  // Validation errors
  const [errors, setErrors] = useState({});
  const [passwordErrors, setPasswordErrors] = useState({});

  const { data: profileResponse, isLoading: loading } = useQuery({
    queryKey: ['profile'],
    queryFn: () => api.get('/accounts/api/auth/profile/'),
  });

  // Populate form when profile data loads
  useEffect(() => {
    if (profileResponse) {
      setProfileData({
        first_name: profileResponse.first_name || '',
        last_name: profileResponse.last_name || '',
        email: profileResponse.email || '',
        phone: profileResponse.profile?.phone || '',
        profile_photo: profileResponse.profile?.photo || null
      });
    }
  }, [profileResponse]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (passwordErrors[name]) {
      setPasswordErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setError(t('profile.invalidImageType'));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError(t('profile.imageTooLarge'));
      return;
    }

    try {
      setUploadingImage(true);
      setError(null);

      const formData = new FormData();
      formData.append('profile_photo', file);

      // Use POST to the dedicated photo upload endpoint
      const data = await api.post('/accounts/api/auth/profile/photo/', formData);


      // Update local state with the new photo URL
      const photoUrl = data.profile?.photo || data.photo;
            setProfileData(prev => ({ ...prev, profile_photo: photoUrl }));
      setImageKey(Date.now()); // Force image refresh
      setSuccess(t('profile.imageUpdated'));

      // Refresh user data in AuthContext to update avatar everywhere
      if (refreshUser) {
                const refreshedUser = await refreshUser();
              }

      // Clear the file input
      e.target.value = '';

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error uploading image:', err);
      // Error messages might be in the error object directly if thrown by apiRequest
      setError(err.message || t('profile.errorUploadingImage'));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteImage = async () => {
    if (!await confirm(t('profile.confirmDeleteImage'))) {
      return;
    }

    try {
      setUploadingImage(true);
      setError(null);

      // Use DELETE to the dedicated photo endpoint
      await api.delete('/accounts/api/auth/profile/photo/');

      setProfileData(prev => ({ ...prev, profile_photo: null }));
      setImageKey(Date.now()); // Force image refresh
      setSuccess(t('profile.imageDeleted'));

      // Refresh user data in AuthContext to update avatar everywhere
      if (refreshUser) {
                await refreshUser();
      }

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting image:', err);
      setError(t('profile.errorDeletingImage'));
    } finally {
      setUploadingImage(false);
    }
  };

  const validateProfileForm = () => {
    const newErrors = {};

    if (!profileData.first_name?.trim()) {
      newErrors.first_name = t('profile.firstNameRequired');
    }

    if (!profileData.last_name?.trim()) {
      newErrors.last_name = t('profile.lastNameRequired');
    }

    if (!profileData.email?.trim()) {
      newErrors.email = t('profile.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileData.email)) {
      newErrors.email = t('profile.emailInvalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePasswordForm = () => {
    const newErrors = {};

    if (!passwordData.old_password) {
      newErrors.old_password = t('profile.oldPasswordRequired');
    }

    if (!passwordData.new_password) {
      newErrors.new_password = t('profile.newPasswordRequired');
    } else if (passwordData.new_password.length < 8) {
      newErrors.new_password = t('profile.passwordTooShort');
    }

    if (!passwordData.confirm_password) {
      newErrors.confirm_password = t('profile.confirmPasswordRequired');
    } else if (passwordData.new_password !== passwordData.confirm_password) {
      newErrors.confirm_password = t('profile.passwordsDoNotMatch');
    }

    setPasswordErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();

    if (!validateProfileForm()) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // api.patch returns data directly
      await api.patch('/accounts/api/auth/profile/', {
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        email: profileData.email,
        phone: profileData.phone
      });

      setSuccess(t('profile.profileUpdated'));

      // Update user context with fresh data
      if (refreshUser) {
        await refreshUser(); // Refresh full user data
      }

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating profile:', err);
      // The error object thrown by apiRequest contains the message
      setError(err.message || t('profile.errorUpdating'));
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (!validatePasswordForm()) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      await api.post('/accounts/api/auth/change-password/', {
        old_password: passwordData.old_password,
        new_password: passwordData.new_password
      });

      setSuccess(t('profile.passwordChanged'));
      setPasswordData({
        old_password: '',
        new_password: '',
        confirm_password: ''
      });

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error changing password:', err);
      // The error message contains the details
      setError(err.message || t('profile.errorChangingPassword'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <CenteredLoader />;
  }


  const getProfileImageUrl = () => {
    if (!profileData.profile_photo) {
      return null;
    }
    // If it's already a full URL, use it
    let photoUrl = profileData.profile_photo;
    if (!photoUrl.startsWith('http')) {
      // Otherwise, prepend the base URL
      photoUrl = `${window.location.origin}${photoUrl}`;
    }

    // Add cache-busting parameter to force browser to reload image
    return photoUrl.includes('?')
      ? `${photoUrl}&t=${imageKey}`
      : `${photoUrl}?t=${imageKey}`;
  };

  return (
    <div className="page-wrapper">
      {/* Page header */}
      <div className="page-header d-print-none">
        <div className="container-fluid px-4">
          <div className="row g-2 align-items-center">
            <div className="col">
              {/* Page pre-title */}
              <div className="page-pretitle">
                {t('profile.subtitle')}
              </div>
              <h2 className="page-title">
                {t('profile.title')}
              </h2>
            </div>
          </div>
        </div>
      </div>

      {/* Page body */}
      <div className="page-body">
        <div className="container-fluid px-4">
          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-3">
              {error}
            </Alert>
          )}

          {success && (
            <Alert variant="success" onClose={() => setSuccess(null)} dismissible className="mb-3">
              {success}
            </Alert>
          )}

          <div className="row row-deck row-cards">
            <div className="col-12 mb-3">
              {/* Profile Photo Card */}
              <Card className="mb-3">
                <Card.Body className="text-center">
                  <div className="mb-3">
                    {profileData.profile_photo ? (
                      <img
                        src={getProfileImageUrl()}
                        alt={t('profile.profilePhoto')}
                        className="rounded-circle"
                        style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(profileData.first_name + ' ' + profileData.last_name)}&size=150&background=206bc4&color=fff`;
                        }}
                      />
                    ) : (
                      <div
                        className="rounded-circle bg-primary text-white d-inline-flex align-items-center justify-content-center"
                        style={{ width: '150px', height: '150px', fontSize: '3rem' }}
                      >
                        {profileData.first_name?.[0]?.toUpperCase() || 'U'}
                        {profileData.last_name?.[0]?.toUpperCase() || ''}
                      </div>
                    )}
                  </div>
                  <h4 className="mb-1">
                    {profileData.first_name} {profileData.last_name}
                  </h4>
                  <p className="text-muted mb-3">{profileData.email}</p>
                  <div className="d-grid gap-2">
                    <Button
                      variant="primary"
                      onClick={() => document.getElementById('profilePhotoInput').click()}
                      disabled={uploadingImage}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-upload me-1" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                        <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2"></path>
                        <path d="M7 9l5 -5l5 5"></path>
                        <path d="M12 4l0 12"></path>
                      </svg>
                      {uploadingImage ? t('profile.uploading') : t('profile.uploadPhoto')}
                    </Button>
                    {profileData.profile_photo && (
                      <Button
                        variant="outline-danger"
                        onClick={handleDeleteImage}
                        disabled={uploadingImage}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-trash me-1" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                          <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                          <path d="M4 7l16 0"></path>
                          <path d="M10 11l0 6"></path>
                          <path d="M14 11l0 6"></path>
                          <path d="M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12"></path>
                          <path d="M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3"></path>
                        </svg>
                        {t('profile.deletePhoto')}
                      </Button>
                    )}
                    <input
                      id="profilePhotoInput"
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handleImageChange}
                      style={{ display: 'none' }}
                    />
                  </div>
                  <small className="text-muted d-block mt-2">
                    {t('profile.photoHint')}
                  </small>
                </Card.Body>
              </Card>
            </div>

            <div className="col-12">
              <Card>
                <Card.Header>
                  <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                    <Nav variant="tabs" className="card-header-tabs">
                      <Nav.Item>
                        <Nav.Link eventKey="general">
                          <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-user me-1" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                            <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0"></path>
                            <path d="M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"></path>
                          </svg>
                          {t('profile.generalInfo')}
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link eventKey="security">
                          <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-lock me-1" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                            <path d="M5 13a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v6a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2v-6z"></path>
                            <path d="M11 16a1 1 0 1 0 2 0a1 1 0 0 0 -2 0"></path>
                            <path d="M8 11v-4a4 4 0 1 1 8 0v4"></path>
                          </svg>
                          {t('profile.security')}
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link eventKey="sessions">
                          <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-devices me-1" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                            <path d="M3 5m0 1a1 1 0 0 1 1 -1h7a1 1 0 0 1 1 1v6a1 1 0 0 1 -1 1h-7a1 1 0 0 1 -1 -1z"></path>
                            <path d="M7 15v2m-2 0h4"></path>
                            <path d="M17 8h3a1 1 0 0 1 1 1v6a1 1 0 0 1 -1 1h-7a1 1 0 0 1 -1 -1v-1"></path>
                          </svg>
                          {t('profile.sessions.title')}
                        </Nav.Link>
                      </Nav.Item>
                    </Nav>
                  </Tab.Container>
                </Card.Header>
                <Card.Body>
                  {activeTab === 'general' && (
                    <Form onSubmit={handleProfileSubmit}>
                      <div className="mb-4">
                        <h4 className="mb-3">{t('profile.personalInformation')}</h4>
                        <Row className="mb-3">
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>{t('profile.firstName')}</Form.Label>
                              <Form.Control
                                type="text"
                                name="first_name"
                                value={profileData.first_name}
                                onChange={handleInputChange}
                                isInvalid={!!errors.first_name}
                                placeholder={t('profile.firstName')}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.first_name}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>{t('profile.lastName')}</Form.Label>
                              <Form.Control
                                type="text"
                                name="last_name"
                                value={profileData.last_name}
                                onChange={handleInputChange}
                                isInvalid={!!errors.last_name}
                                placeholder={t('profile.lastName')}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.last_name}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </Row>
                      </div>

                      <div className="mb-4">
                        <h4 className="mb-3">{t('profile.contactInformation')}</h4>
                        <Row className="mb-3">
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>{t('profile.email')}</Form.Label>
                              <Form.Control
                                type="email"
                                name="email"
                                value={profileData.email}
                                onChange={handleInputChange}
                                isInvalid={!!errors.email}
                                placeholder={t('profile.email')}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.email}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>{t('profile.phone')}</Form.Label>
                              <Form.Control
                                type="tel"
                                name="phone"
                                value={profileData.phone}
                                onChange={handleInputChange}
                                isInvalid={!!errors.phone}
                                placeholder={t('profile.phone')}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.phone}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </Row>
                      </div>

                      <hr className="my-4" />

                      <div className="d-flex justify-content-end">
                        <Button
                          type="submit"
                          variant="primary"
                          disabled={saving}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-device-floppy me-1" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                            <path d="M6 4h10l4 4v10a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2"></path>
                            <path d="M12 14m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0"></path>
                            <path d="M14 4l0 4l-6 0l0 -4"></path>
                          </svg>
                          {saving ? t('profile.saving') : t('profile.saveChanges')}
                        </Button>
                      </div>
                    </Form>
                  )}

                  {activeTab === 'security' && (
                    <Form onSubmit={handlePasswordSubmit}>
                      <div className="mb-4">
                        <h4 className="mb-2">{t('profile.changePassword')}</h4>
                        <p className="text-muted">{t('profile.changePasswordDescription')}</p>
                      </div>

                      <Row className="mb-3">
                        <Col md={12}>
                          <Form.Group>
                            <Form.Label>{t('profile.oldPassword')}</Form.Label>
                            <Form.Control
                              type="password"
                              name="old_password"
                              value={passwordData.old_password}
                              onChange={handlePasswordChange}
                              isInvalid={!!passwordErrors.old_password}
                              placeholder={t('profile.oldPassword')}
                            />
                            <Form.Control.Feedback type="invalid">
                              {passwordErrors.old_password}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>{t('profile.newPassword')}</Form.Label>
                            <Form.Control
                              type="password"
                              name="new_password"
                              value={passwordData.new_password}
                              onChange={handlePasswordChange}
                              isInvalid={!!passwordErrors.new_password}
                              placeholder={t('profile.newPassword')}
                            />
                            <Form.Control.Feedback type="invalid">
                              {passwordErrors.new_password}
                            </Form.Control.Feedback>
                            <Form.Text className="text-muted">
                              {t('profile.passwordHint')}
                            </Form.Text>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>{t('profile.confirmPassword')}</Form.Label>
                            <Form.Control
                              type="password"
                              name="confirm_password"
                              value={passwordData.confirm_password}
                              onChange={handlePasswordChange}
                              isInvalid={!!passwordErrors.confirm_password}
                              placeholder={t('profile.confirmPassword')}
                            />
                            <Form.Control.Feedback type="invalid">
                              {passwordErrors.confirm_password}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>

                      <hr className="my-4" />

                      <div className="d-flex justify-content-end">
                        <Button
                          type="submit"
                          variant="primary"
                          disabled={saving}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-key me-1" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                            <path d="M16.555 3.843l3.602 3.602a2.877 2.877 0 0 1 0 4.069l-2.643 2.643a2.877 2.877 0 0 1 -4.069 0l-.301 -.301l-6.558 6.558a2 2 0 0 1 -1.239 .578l-.175 .008h-1.172a1 1 0 0 1 -.993 -.883l-.007 -.117v-1.172a2 2 0 0 1 .467 -1.284l.119 -.13l.414 -.414h2v-2h2v-2l2.144 -2.144l-.301 -.301a2.877 2.877 0 0 1 0 -4.069l2.643 -2.643a2.877 2.877 0 0 1 4.069 0z"></path>
                            <path d="M15 9h.01"></path>
                          </svg>
                          {saving ? t('profile.saving') : t('profile.changePassword')}
                        </Button>
                      </div>
                    </Form>
                  )}

                  {activeTab === 'sessions' && <ActiveSessionsTab />}
                </Card.Body>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
